<template>
    <div class="relative pb-8 -mt-4 z-50">
        <!-- Search disabled for now -->
<!--         <div class="absolute top-0 right-0 flex justify-end items-center">
            <i class="fa fa-search mr-3 text-gray-500"></i>

            <input-text
                class="w-full md:w-64 lg:w-72 xl:w-80"
                input_class="h-10 w-full"
                label_class="text-xs text-gray-600 uppercase tracking-widest inline-block leading-snug font-semibold"
                placeholder="Search all documents"
                v-model="filter"
            />
        </div> -->

        <div v-if="includes_docusign" class="flex flex-1">
            <document-tabs
                current_tab="documents"
                :documents_index_route="documents_index_route"
                :signatures_index_route="signatures_index_route"
            />
        </div>

        <div v-else class="h-12"></div>

        <!-- Button bar -->
        <div class="w-full flex justify-end items-center space-x-4 bg-gray-100 px-4 py-4 my-4 border border-gray-300">
            <button-blue-outline
                v-if="! read_only"
                @click="addFolder"
            >
                <div class="flex items-center">
                    <folder-plus-icon stroke-width="2" size="14" class="mb-0.5 -ml-1 mr-3" />

                    <div>
                        {{ $t('New Folder') }}
                    </div>
                </div>
            </button-blue-outline>

            <button-blue-outline
                v-if="! read_only"
                @click="showUploadFolderSelectModal"
            >
                <div class="flex items-center">
                    <upload-icon stroke-width="2" size="14" class="mb-0.5 -ml-1 mr-3" />

                    <div>
                        {{ $t('Upload a Document') }}
                    </div>
                </div>
            </button-blue-outline>
        </div>

        <!-- Folder tree -->
        <div class="w-full min-h-screen">
            <div class="w-full mb-4">
                <document-folder-tree
                    :folders="document_folders_proxy"
                    :has_filter="!! filter"
                    :property="property"
                    :documents="documents_proxy"
                    :templates="templates_proxy"
                    :read_only="read_only"
                    :has_active_docusign_user="has_active_docusign_user"
                    :store_route="store_route"
                    :folder_to_expand="folder_to_expand"
                    @click="clearFilter"
                    @updated_folder="updateFolder"
                    @deleted_folder="deleteFolder"
                    @expanded_folder="expandedFolder"
                    @updated_document="updatedDocument"
                    @documentEdited="documentEdited"
                    @documentDeleted="documentDeleted"
                    @documentUploaded="documentUploaded"
                    @editFolder="editFolder"
                    @folders-reordered="foldersReordered"
                    @files-reordered="filesReordered"
                />
            </div>
        </div>

        <document-folder-create-modal
            :show="show_create_folder_modal"
            :property_id="property.id"
            @closed="createFolderModalClosed"
            @createFolder="createFolder"
        />

        <document-upload-folder-select-modal
            :show="show_upload_folder_select_modal"
            :folders="document_folders_proxy"
            :property="property"
            :store_route="store_route"
            @closed="folderUploadSelectModalClosed"
            @documentUploaded="documentUploaded"
            @expandFolder="expandFolder"
        />
    </div>
</template>

<script>
import ButtonBlueOutline from '../../components/button-blue-outline.vue';
import DocumentFolderCreateModal from './document-folder-create-modal.vue';
import DocumentFolderEditModal from './document-folder-edit-modal.vue';
import DocumentFolderTree from './document-folder-tree.vue';
import DocumentTabs from './document-tabs.vue';
import DocumentUploadFolderSelectModal from './document-upload-folder-select-modal.vue';
import { orderBy } from 'lodash';
import { FolderIcon, FolderPlusIcon, UploadIcon } from 'vue-feather-icons';

export default {
    components: {
        ButtonBlueOutline,
        DocumentFolderCreateModal,
        DocumentFolderEditModal,
        DocumentFolderTree,
        DocumentTabs,
        DocumentUploadFolderSelectModal,
        FolderIcon,
        FolderPlusIcon,
        UploadIcon,
    },

    props: {
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        read_only: { default: false },
        includes_docusign: { default: false },
        has_active_docusign_user: { default: false },
        docusign_event: {},
        store_route: {},
        documents_index_route: {},
        signatures_index_route: {},
    },

    data() {
        return {
            documents_proxy: this.documents || [],
            templates_proxy: this.templates || [],
            document_folders_proxy: this.document_folders || [],
            filter: null,
            show_edit_folder_modal: false,
            show_upload_folder_select_modal: false,
            show_create_folder_modal: false,
            folder_to_expand: null,
        }
    },

    computed: {
        filtered_documents() {
            return this.filter
                ? this.documents_proxy.filter(document => document.display_filename.toLowerCase().includes(this.filter.toLowerCase()))
                : this.documents_proxy;
        },
        sorted_documents() {
            return orderBy(this.filtered_documents, 'display_filename', 'asc');
        },
        documents_to_display() {
            // When filtering, show all matching documents
            // When not filtering, documents are displayed within each expanded folder
            return this.filter ? this.sorted_documents : [];
        },
    },

    methods: {
        getDocuments() {
            axios.get(this.documents_index_route)
                .then(response => {
                    this.documents_proxy = response.data?.data;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        getDocumentById(id) {
            return this.documents.find(document => document.id === parseInt(id));
        },
        documentUploaded(document) {
            this.documents_proxy.push(document);
        },
        documentDeleted(id) {
            // Remove the deleted document from the local array
            const index = this.documents_proxy.findIndex(document => document.id === parseInt(id));
            if (index !== -1) {
                this.documents_proxy.splice(index, 1);
            }
        },
        documentEdited(updatedDocument) {
            // Update the document in the local array with the new data
            this.updatedDocument(updatedDocument);
        },
        updatedDocument(document) {
            let index = this.documents_proxy.findIndex(document_proxy => document_proxy.id === document.id);
            this.documents_proxy.splice(index, 1, document);
        },
        updateFolder(folder) {
            this.document_folders_proxy.find(folder_proxy => folder_proxy.id === folder.id).name = folder.name;
        },
        createFolder(folder) {
            window.location.reload();
        },
        deleteFolder(folder) {
            this.document_folders_proxy.splice(
                this.document_folders_proxy.findIndex(folder_proxy => folder_proxy.id === folder.id), 1
            );
        },
        expandedFolder(folder) {
            this.folder_to_expand = null;
        },
        clearFilter() {
            this.filter = null;
        },
        editFolder() {
            this.show_edit_folder_modal = true;
        },
        editFolderModalClosed() {
            this.show_edit_folder_modal = false;
        },
        foldersReordered(folders) {
            this.document_folders_proxy = folders;
        },
        filesReordered(documents) {
            // Update the documents list with the reordered documents
            // Ensure we preserve all attributes including accessors
            documents.forEach(updatedDoc => {
                const index = this.documents_proxy.findIndex(doc => doc.id === updatedDoc.id);
                if (index !== -1) {
                    // Merge the updated document with existing attributes to preserve accessors
                    const existingDoc = this.documents_proxy[index];
                    const mergedDoc = { ...existingDoc, ...updatedDoc };
                    this.documents_proxy.splice(index, 1, mergedDoc);
                }
            });
        },
        showUploadFolderSelectModal() {
            this.show_upload_folder_select_modal = true;
        },
        folderUploadSelectModalClosed() {
            this.show_upload_folder_select_modal = false;
        },
        expandFolder(folder_id) {
            this.folder_to_expand = folder_id;
        },
        addFolder() {
            this.show_create_folder_modal = true;
        },
        createFolderModalClosed() {
            this.show_create_folder_modal = false;
        },
    },
}
</script>
